import * as React from 'react';
import withStyles from 'react-jss';
import {
  Row,
  Col,
  Form,
  Input,
  Select,
  message,
  Checkbox,
  Button,
} from 'src/Library/AntdLibComponents';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import { ContainerTriangulationOnboardingStyles } from './ContainerTriangulationOnboarding.styles';
import { useGenericState } from 'src/Library/UseGenericState';
import { locationTypes, inchargeOptions } from '../Organisation/consignorAddressConstants';
import Radio from 'antd/lib/radio';
import { GOOGLE_PLACES_API_URL } from 'src/globals';
import { searchCountry } from 'src/api/country';
import { useDebounce } from 'src/hooks/useDebounce';
import { Country } from 'src/components/common/Types/CommonTypes';
import { renderSelectDropDown } from 'src/Library/Common';
import Divider from 'antd/lib/divider';
import { fetchUsersV3, createAddress, updateAddress } from 'src/api/organisationProfiling';
import Users from '../TeamManagement/TypesInTeams/Users';
import { isValidArray, isValidString } from 'src/Library/Helper';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import FieldMultiSelectPOL from '../RFQ/common/FieldMultiSelectPOL';

interface IProps extends StylesProps<ReturnType<typeof ContainerTriangulationOnboardingStyles>> {
  form;
  isAddingAddressForm?: boolean;
  isAddingAddressOnMap?: boolean;
  setLocationFields: (props) => void;
  addressType?: string;
  prefillingAddress;
  consignorAddressTypes: KeyLabel[];
  handleCancel?: (isSuccess?: boolean) => void;
  isConsignerAddressModal?: boolean;
}

interface StateProps {
  mapLocation?;
  location?: string;
  formattedAddress?: string;
  searchTerm?: string;
  countries?: Country[];
  isSearching: boolean;
  users: Users[];
  isLoading?: boolean;
}

const { useEffect } = React;

let autocomplete;

declare let google: any;

const AddEditAddressForm = (props: IProps): React.ReactElement => {
  const {
    classes,
    form,
    isAddingAddressForm,
    isAddingAddressOnMap,
    setLocationFields,
    addressType,
    prefillingAddress,
    consignorAddressTypes,
    handleCancel,
    isConsignerAddressModal,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    mapLocation: {
      latLng: {
        lat: prefillingAddress?.latLng?.lat || 28.6448,
        lng: prefillingAddress?.latLng?.lng || 77.216721,
      },
      location: prefillingAddress?.latLng,
    },
    isSearching: false,
    searchTerm: '',
    users: [],
    formattedAddress: prefillingAddress?.formattedAddress,
  });

  const {
    mapLocation,
    location,
    formattedAddress,
    searchTerm,
    countries,
    isSearching,
    users,
    isLoading,
  } = state;

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    if (!isAddingAddressForm) {
      document.getElementById('googleMapsAPI')?.remove();
      const script = document.createElement('script');
      script.src = GOOGLE_PLACES_API_URL;
      script.async = true;
      script.onload = getPlaces;
      script.id = 'googleMapsAPI';
      document.body.appendChild(script);
    }
  }, [isAddingAddressForm]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    searchCountryList(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const searchCountryList = async (queryString: string = '') => {
    let countries: Country[] = [];
    setState({ isSearching: true });
    const response = await searchCountry({ queryString });
    if (response.isSuccess) {
      countries = response.data || [];
    }
    setState({ countries, isSearching: false });
  };

  // For each place, get the icon, name and location.
  const getIcon = () => ({
    url:
      'https://shipsy-public-assets.s3-us-west-2.amazonaws.com/shipper-dashboard/icons/marker.png',
    size: new google.maps.Size(71, 71),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(17, 34),
    scaledSize: new google.maps.Size(25, 25),
  });

  const getPlaces = (savedPlace?) => {
    const input = document.getElementById('autocomplete');
    const options = {
      types: ['establishment'],
    };

    autocomplete = new google.maps.places.Autocomplete(input, options);

    const map = new google.maps.Map(document.getElementById('googleMap'), {
      center: { lat: mapLocation?.latLng?.lat, lng: mapLocation?.latLng?.lng },
      zoom: 1,
      mapTypeId: 'roadmap',
      maxZoom: 18,
    });

    let markers: any[] = [];
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place) {
        return;
      }
      // Clear out the old markers.
      markers.forEach((marker) => {
        marker.setMap(null);
      });
      markers = [];
      const bounds = new google.maps.LatLngBounds();
      if (!place.geometry) {
        console.log('Returned place contains no geometry');
        return;
      }
      handlePlaceChanged(place);
      // Create a marker for each place.
      markers.push(
        new google.maps.Marker({
          map,
          icon: getIcon(),
          title: place.name,
          label: place.name,
          position: place.geometry.location,
        })
      );

      if (place.geometry.viewport) {
        // Only geocodes have viewport.
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
      map.fitBounds(bounds);
      return;
    });
    const bounds = new google.maps.LatLngBounds();
    markers.push(
      new google.maps.Marker({
        map,
        icon: getIcon(),
        title: savedPlace.name,
        label: savedPlace.name,
        position: savedPlace.location || {
          lat: mapLocation?.latLng?.lat,
          lng: mapLocation?.latLng?.lng,
        },
      })
    );
    if (savedPlace.viewport) {
      // Only geocodes have viewport.
      bounds.union(savedPlace.viewport);
    } else {
      bounds.extend(
        savedPlace.location || { lat: mapLocation?.latLng?.lat, lng: mapLocation?.latLng?.lng }
      );
    }
    map.fitBounds(bounds);
  };

  const handlePlaceChanged = (place) => {
    setState({
      location: `${place.name} ${place.formatted_address}`,
      formattedAddress: place.formatted_address,
    });

    const address: any = {};
    place.address_components &&
      place.address_components.forEach((item) => {
        address[item.types[0]] = item.long_name;
      });

    const latLng = {
      lat: place?.geometry?.location?.lat() || mapLocation?.latLng?.lat,
      lng: place?.geometry?.location?.lng() || mapLocation?.latLng?.lng,
    };

    const location = {
      latLng,
      name: place.name,
      city: address.locality,
      address: place.vicinity,
      state: address.administrative_area_level_1,
      country: address.country,
      pincode: address.postal_code,
      viewPort: place.geometry.viewport,
      location: place.geometry.location,
    };

    setState({ mapLocation: location });

    if (!isAddingAddressOnMap && !isAddingAddressForm && !addressType) {
      form.setFieldsValue({
        name: location?.name,
        address: location?.address,
        landmark: undefined,
        pincode: location?.pincode,
        city: location?.city,
        state: location?.state,
        /* isConsignerAddressModal check because country field in consignor address modal is only for key
        while in other places its key label */
        countryId: location?.country
          ? isConsignerAddressModal
            ? location?.country
            : { key: location?.country, label: location?.country }
          : undefined,
      });
    }

    if (setLocationFields) {
      setLocationFields({
        [`${isConsignerAddressModal ? 'addressObject' : addressType}`]: {
          mapLocation: location,
          formattedAddress: place.formatted_address,
        },
      });
    }
  };

  const renderGoogleLocation = () => {
    return (
      <Row gutter={24}>
        <Col
          span={4}
          className={classes.addressLabel}
          style={{ fontWeight: isConsignerAddressModal ? 600 : 'normal' }}
        >
          Map Location*
        </Col>
        <Col span={8}>
          <Input.Search
            id="autocomplete"
            placeholder={'Search Location'}
            value={location}
            onChange={(e) => {
              setState({
                location: e.target.value,
              });
            }}
            className={isConsignerAddressModal ? undefined : classes.inputSearch}
            style={{ width: '100%', marginRight: '4%' }}
          ></Input.Search>
          {formattedAddress && renderSelectedAddress()}
        </Col>
        <Col span={12}>
          <div className={classes.googleMapDiv} id="googleMap"></div>
        </Col>
      </Row>
    );
  };

  const renderSelectedAddress = (): React.ReactElement => {
    return (
      <div style={{ marginTop: '10px' }}>
        <span style={{ fontSize: '12px' }}>Select Location</span>
        <div className={classes.selectedAddress}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111111' }}>
            {mapLocation?.name || mapLocation?.city || prefillingAddress?.name}
          </div>
          <div style={{ fontSize: '10px', lineHeight: '12px', marginTop: '5px' }}>
            {formattedAddress}
          </div>
        </div>
      </div>
    );
  };

  const renderLocationType = (): React.ReactElement => {
    return (
      <Row>
        <Col span={4} className={classes.addressLabel} style={{ marginTop: '6px' }}>
          Location Type*
        </Col>
        <Col span={20}>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}locationType`, {
              rules: [{ required: true, message: 'Required' }],
              initialValue: prefillingAddress?.locationType || 'WAREHOUSE',
            })(
              <Radio.Group style={{ fontSize: '12px', width: '100%' }} className={classes.radio}>
                {locationTypes?.map((item) => (
                  <Col span={5}>
                    <Radio key={item.value} value={item.value}>
                      {item.label}
                    </Radio>
                  </Col>
                ))}
              </Radio.Group>
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderName = (): React.ReactElement => {
    return (
      <Row>
        <Col span={4} className={classes.addressLabel}>
          Name*
        </Col>
        <Col span={10}>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}name`, {
              rules: [{ required: true, message: 'Required' }],
              initialValue: prefillingAddress?.name || undefined,
            })(<Input placeholder={'Organisation Name'} className={classes.inputField} />)}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderAddress = (): React.ReactElement => {
    return (
      <Row style={{ marginTop: '8px' }}>
        <Col span={4} className={classes.addressLabel}>
          Address Details*
        </Col>
        <Col span={20}>
          <Row>
            <Col span={12}>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}address`, {
                  rules: [{ required: true, message: 'Required' }],
                  initialValue: prefillingAddress?.address || undefined,
                })(<Input placeholder={'Address'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item style={{ paddingLeft: '10px', marginBottom: 0 }}>
                {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}landmark`, {
                  initialValue: prefillingAddress?.landmark || undefined,
                })(<Input placeholder={'Landmark'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}pincode`, {
                  initialValue: prefillingAddress?.pincode || undefined,
                })(<Input placeholder={'Pincode'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item style={{ marginBottom: 0, paddingLeft: '10px' }}>
                {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}city`, {
                  initialValue: prefillingAddress?.city || undefined,
                })(<Input placeholder={'City'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 0, paddingLeft: '10px' }}>
                {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}state`, {
                  initialValue: prefillingAddress?.state || undefined,
                })(<Input placeholder={'State'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={6}>{renderCountryField()}</Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderCountryField = (): React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0, paddingLeft: '10px' }}>
        {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}countryId`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue: isValidString(prefillingAddress?.country)
            ? {
                key: prefillingAddress?.country,
                label: prefillingAddress?.country,
              }
            : prefillingAddress?.countryId
            ? { key: prefillingAddress?.countryId, label: prefillingAddress?.countryName }
            : undefined,
        })(
          <Select
            style={{ width: '100%' }}
            showSearch
            labelInValue
            className={classes.selectField}
            filterOption={(input, option) => true}
            onFocus={() => setState({ searchTerm: '' })}
            onSearch={(searchTerm) => setState({ searchTerm })}
            dropdownMatchSelectWidth={false}
            dropdownRender={(menu) => renderSelectDropDown(menu, isSearching)}
            placeholder="Select Country"
          >
            {countries?.map((item) => (
              <Select.Option key={item.id}>{item.countryName}</Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderInchargeContactDetails = (): React.ReactElement => {
    return (
      <Row style={{ marginTop: '8px' }}>
        <Col span={4} className={classes.addressLabel}>
          Incharge Contact Details*
        </Col>
        <Col span={20}>{renderInchargeContactType()}</Col>
      </Row>
    );
  };

  const renderInchargeContactType = (): React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}incharge`, {
          initialValue: prefillingAddress?.inchargeContactName ? 'other' : 'existing',
        })(
          <Radio.Group style={{ width: '100%' }}>
            {inchargeOptions?.map((item) => (
              <Radio
                key={item.value}
                value={item.value}
                className={classNames(classes.inchargeRadio, classes.radio)}
                onChange={(e) => {
                  if (e.target.value === 'other') {
                    form.setFieldsValue({
                      [`${addressType ? `${addressType}.` : ''}inchargeUserId`]: undefined,
                    });
                  }
                }}
              >
                {renderInchargeField(item.value)}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </Form.Item>
    );
  };

  const fetchUserList = async (): Promise<void> => {
    if (!isValidArray(users)) {
      setState({ isSearching: true });
      let users = [];
      const response = await fetchUsersV3();
      if (response.isSuccess) {
        users = response.data || [];
      } else {
        message.error(response.errorMessage);
      }
      setState({ users, isSearching: false });
    }
  };

  const renderInchargeField = (key): React.ReactElement => {
    if (key === 'existing') {
      const isSelectUserDisabled =
        form.getFieldValue(`${addressType ? `${addressType}.` : ''}incharge`) !== 'existing';
      return (
        <Form.Item style={{ marginBottom: 0 }}>
          {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}inchargeUserId`, {
            rules: [{ required: !isSelectUserDisabled, message: 'Required' }],
            initialValue: prefillingAddress?.inchargeUserId || undefined,
          })(
            <Select
              style={{ width: '55%' }}
              showSearch
              className={classes.selectField}
              optionFilterProp="children"
              onFocus={() => fetchUserList()}
              dropdownMatchSelectWidth={false}
              disabled={isSelectUserDisabled}
              dropdownRender={(menu) => renderSelectDropDown(menu, isSearching)}
              placeholder="Select"
            >
              {users?.map((user) => (
                <Select.Option key={user.id}>{user.name || user.email}</Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      );
    }
    return renderNewInchargeUser();
  };

  const renderNewInchargeUser = (): React.ReactElement => {
    return (
      <React.Fragment>
        <div style={{ marginTop: '8px' }}>Other</div>
        {form.getFieldValue(`${addressType ? `${addressType}.` : ''}incharge`) === 'other' && (
          <Row style={{ margin: '0 -3% 0 -4%' }}>
            <Col span={6}>
              <div className={classes.addressLabel}>User Name*</div>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(
                  `${addressType ? `${addressType}.` : ''}inchargeContactName`,
                  {
                    rules: [{ required: true, message: 'Required' }],
                    initialValue: prefillingAddress?.inchargeContactName || undefined,
                  }
                )(<Input placeholder={'User Name'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={6} style={{ paddingLeft: '10px' }}>
              <div className={classes.addressLabel}>Phone*</div>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(
                  `${addressType ? `${addressType}.` : ''}inchargeContactPhone`,
                  {
                    rules: [{ required: true, message: 'Required' }],
                    initialValue: prefillingAddress?.inchargeContactPhone || undefined,
                  }
                )(<Input placeholder={'Phone'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
            <Col span={12} style={{ paddingLeft: '10px' }}>
              <div className={classes.addressLabel}>Email*</div>
              <Form.Item style={{ marginBottom: 0 }}>
                {form.getFieldDecorator(
                  `${addressType ? `${addressType}.` : ''}inchargeContactEmail`,
                  {
                    rules: [
                      { required: true, message: 'Required' },
                      { type: 'email', message: 'Please enter a valid Email Id' },
                    ],
                    initialValue: prefillingAddress?.inchargeContactEmail || undefined,
                  }
                )(<Input placeholder={'Email'} className={classes.inputField} />)}
              </Form.Item>
            </Col>
          </Row>
        )}
      </React.Fragment>
    );
  };

  const renderDivider = () => {
    return <Divider style={{ margin: '15px 0px' }} />;
  };

  const renderAddressType = (): React.ReactElement => {
    return (
      <Row>
        <Col span={4} className={classes.addressLabel} style={{ marginTop: '6px' }}>
          Address Type*
        </Col>
        <Col span={20}>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator(`${addressType ? `${addressType}.` : ''}addressTypes`, {
              rules: [{ required: true, message: 'Required' }],
              initialValue: prefillingAddress?.addressTypes || undefined,
            })(
              <Checkbox.Group className={classes.checkbox} style={{ width: '100%' }}>
                {consignorAddressTypes?.map((item) => (
                  <Col span={5}>
                    <Checkbox key={item.key} value={item.key}>
                      {item.label}
                    </Checkbox>
                  </Col>
                ))}
              </Checkbox.Group>
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderForm = (): React.ReactElement => {
    if (isConsignerAddressModal) {
      return renderGoogleLocation();
    }

    if (isAddingAddressOnMap) {
      return (
        <React.Fragment>
          {renderLocationType()}
          {renderGoogleLocation()}
        </React.Fragment>
      );
    }

    if (isAddingAddressForm) {
      return (
        <React.Fragment>
          {renderName()}
          {renderAddress()}
          {renderDivider()}
          {renderInchargeContactDetails()}
        </React.Fragment>
      );
    }

    const handleUpdate = (): void => {
      form.validateFieldsAndScroll(async (err, formValues) => {
        if (err) {
          return;
        }
        if (!mapLocation?.latLng?.lat || !mapLocation?.latLng?.lng || !mapLocation?.location) {
          message.error('Please select location on google map');
          return;
        }
        setState({ isLoading: true });
        const body = {
          ...formValues,
          location: 'FACTORY',
          countryId:
            formValues?.countryId?.key === formValues?.countryId?.label
              ? undefined
              : formValues?.countryId
                  ?.key /* country name will be sent if the country is prefilled from the map
            since we will not get the country id from googlemap */,
          countryName:
            formValues?.countryId?.key === formValues?.countryId?.label
              ? formValues?.countryId?.key
              : undefined,
          latitude: mapLocation?.latLng?.lat,
          longitude: mapLocation?.latLng?.lng,
        };
        const response = isEmpty(prefillingAddress)
          ? await createAddress(body)
          : await updateAddress(prefillingAddress?.id, body);
        if (response.isSuccess) {
          message.success('Address added successfully');
          setState({ isLoading: false });
          handleCancel && handleCancel(true);
        } else {
          message.error(response.errorMessage);
          setState({ isLoading: false });
        }
      });
    };

    const renderFooter = (): React.ReactElement => {
      return (
        <div className={classes.footer} style={{ backgroundColor: '#FFFFFF' }}>
          <Button className={classes.nextButton} onClick={handleUpdate} loading={isLoading}>
            {isEmpty(prefillingAddress) ? 'Add' : 'Update'}
          </Button>
          <span className={classes.cancelButton} onClick={() => handleCancel && handleCancel()}>
            Cancel
          </span>
        </div>
      );
    };

    const renderFinalOriginDestinationPort = ({
      required = false,
      isDisabled = false,
      isFinalPol,
    }: {
      required?: boolean;
      isDisabled?: boolean;
      isFinalPol: boolean;
    }) => {
      const formLayout = {
        wrapperCol: { span: 8 },
      };

      const params = isFinalPol
        ? {
            label: 'Final POL',
            inputKey: 'finalPolId',
            initialValue: undefined,
          }
        : {
            label: 'Final POD',
            inputKey: 'finalPodId',
            initialValue: undefined,
          };

      const { label, inputKey, initialValue } = params;

      return (
        <Row>
          <Col span={4} className={classes.addressLabel} style={{ marginTop: '6px' }}>
            {label}
          </Col>
          <Col span={12}>
            <Form.Item style={{ marginBottom: 0 }}>
              {form.getFieldDecorator(inputKey, {
                initialValue,
                rules: [{ required, message: 'Cannot be empty!' }],
              })(
                <FieldMultiSelectPOL
                  isWet={true}
                  isAir={true}
                  isDry={true}
                  allowedPortTypes={['DRY', 'WET', 'AIR']}
                  placeholder={label}
                  singleMode={true}
                  initialValueIds={undefined}
                  disabled={isDisabled}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      );
    };

    return (
      <React.Fragment>
        <div style={{ padding: '24px 24px 7% 24px' }}>
          {renderLocationType()}
          {renderAddressType()}
          {renderDivider()}
          {renderGoogleLocation()}
          {renderDivider()}
          {renderName()}
          {renderAddress()}
          {renderDivider()}
          {renderInchargeContactDetails()}
          {/* {renderDivider()}
          {renderFinalOriginDestinationPort({ isFinalPol: true })}
          {renderFinalOriginDestinationPort({ isFinalPol: false })} */}
        </div>
        {renderFooter()}
      </React.Fragment>
    );
  };

  return <div>{renderForm()}</div>;
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  consignorAddressTypes: masterData.consignorAddressTypes,
});

export default connect(
  mapStateToProps,
  null
)(
  React.memo(withStyles(ContainerTriangulationOnboardingStyles)(Form.create()(AddEditAddressForm)))
);
